import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Users } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const HERO_IMG = "https://images.unsplash.com/photo-1593351799227-75df2026356b?auto=format&fit=crop&w=1800&q=85";
const FACTORY_IMG = "/faytex-factory.webp";
const MDN_IMG = "/mother-daughter-nation.webp";
const MARKET_IMG  = "/home-market.webp";
const ATHLETE_IMG = "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1400&q=80";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [introDone, setIntroDone] = useState(false);
  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative isolate overflow-hidden"
      >
        {/* 4K intro video — plays first, then fades to reveal the static hero image */}
        <div
          aria-hidden={introDone}
          className={`absolute inset-0 z-20 bg-black transition-opacity duration-1000 ${
            introDone ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <video
            data-testid="hero-intro-video"
            src="/hero-video.mp4"
            poster="/hero-poster.jpg"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={() => setIntroDone(true)}
            onError={() => setIntroDone(true)}
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={() => setIntroDone(true)}
            data-testid="hero-intro-skip"
            className="absolute bottom-6 right-6 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white text-xs uppercase tracking-widest font-semibold px-4 py-2 ring-1 ring-white/25"
          >
            Skip intro
          </button>
        </div>

        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="Faytex Kpone factory" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-48 lg:py-56">
          <div className="max-w-2xl text-white animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-widest font-medium">
              <Sparkles className="w-3.5 h-3.5" /> Made in Kpone, Ghana
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl mt-6 leading-[0.95] tracking-tighter">
              Faytex: <span className="text-faytex-pink">The Pride of Ghana</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/90 max-w-xl leading-relaxed">
              A heritage of confidence, woven from mother to daughter — and into every pad we make.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="group inline-flex items-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover px-7 py-3.5 text-base font-semibold text-white transition-colors"
              >
                Shop Our Pads <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/legacy"
                data-testid="hero-legacy-button"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur px-7 py-3.5 text-base font-semibold text-white ring-1 ring-white/30 transition-colors"
              >
                Our Mother-to-Daughter Story
              </Link>
              <Link
                to="/market-queens"
                data-testid="hero-queens-button"
                className="inline-flex items-center gap-2 rounded-full text-white/90 hover:text-white px-3 py-3.5 text-base font-medium underline-offset-4 hover:underline"
              >
                Meet Our Market Queens
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 md:-mt-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: <Heart className="w-5 h-5" />, k: "Heritage", v: "From founder Sara Galloway to her daughter Jeanine Hage-Ali — four decades of Ghanaian care." },
            { icon: <Sparkles className="w-5 h-5" />, k: "Quality", v: "Clinically trusted and thoughtfully designed, every Faytex product is manufactured in our modern production facility in Kpone, Ghana, to deliver dependable protection women can rely on." },
            { icon: <Users className="w-5 h-5" />, k: "Community", v: "Trusted by women and supported by dedicated Market Queens, distributors and retail partners across Ghana, Faytex reaches communities where confidence matters most." },
          ].map((p, i) => (
            <div
              key={p.k}
              data-testid={`pillar-${i}`}
              className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100 hover:shadow-hover transition-shadow"
            >
              <div className="w-10 h-10 rounded-full bg-faytex-pinkLight text-faytex-pink flex items-center justify-center">
                {p.icon}
              </div>
              <h3 className="font-heading text-2xl mt-4">{p.k}</h3>
              <p className="text-sm text-faytex-muted mt-2 leading-relaxed">{p.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32" data-testid="home-products-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Shop our pads</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-2 tracking-tight">Confidence, every day of the month.</h2>
          </div>
          <Link to="/shop" className="text-sm font-semibold text-faytex-pink hover:underline">View all →</Link>
        </div>

        {/* Family product shot */}
        <div className="mb-12 rounded-3xl overflow-hidden shadow-hover bg-white">
          <img
            src="/faytex-full-range.webp"
            alt="The full Faytex product range — Classic, Eve Labour Pad, Fluffy Multi-Pack and Fluffy Extra Long"
            className="w-full h-auto object-cover"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 4).map((p) => (
            <Link
              key={p.id}
              to={`/shop/${p.id}`}
              data-testid={`home-product-${p.id}`}
              className="group block rounded-2xl border border-gray-100 bg-white shadow-soft hover:shadow-hover transition-all overflow-hidden"
            >
              <div className="h-1.5 w-full" style={{ background: p.color }} />
              <div className="aspect-square bg-faytex-pinkLight/40 flex items-center justify-center p-8 overflow-hidden">
                <img src={resolveImg(p.image)} alt={p.name} className="max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <p className="text-[11px] uppercase tracking-widest text-faytex-muted">{p.line}</p>
                <h3 className="font-heading text-xl mt-1">{p.name}</h3>
                <p className="text-sm text-faytex-muted mt-1 line-clamp-2">{p.tagline}</p>
                <p className="text-sm font-semibold mt-3">GH₵ {p.price_ghs}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LEGACY STRIP */}
      <section className="bg-faytex-pinkLight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={MDN_IMG} alt="Sara and Jeanine — a mother and daughter, and the Faytex heritage" className="rounded-3xl shadow-hover w-full h-[420px] object-cover" style={{objectPosition: "center 20%"}} />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-white rounded-2xl shadow-hover p-5 max-w-[220px]">
              <p className="font-heading text-2xl text-faytex-pink">2021</p>
              <p className="text-xs text-faytex-muted mt-1">The fire. The rebirth. Stronger than ever.</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">The Faytex Legacy</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-2 tracking-tight">A mother. A daughter. A nation.</h2>
            <p className="mt-5 text-faytex-muted leading-relaxed">
              From founder Sara Galloway to her daughter Jeanine Hage-Ali, Faytex has grown into a symbol of
              resilience for Ghanaian women — anchored by a new factory and the hundreds of hands that build it.
            </p>
            <Link
              to="/legacy"
              data-testid="legacy-cta"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-ink text-white px-6 py-3 text-sm font-semibold hover:bg-faytex-pink transition-colors"
            >
              Read the legacy <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* REIMAGINED + QUEENS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-6">
        <Link
          to="/reimagined"
          data-testid="home-card-reimagined"
          className="group relative overflow-hidden rounded-3xl h-[420px]"
        >
          <img src={ATHLETE_IMG} alt="Black Queens athlete" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 text-white">
            <p className="text-xs uppercase tracking-widest text-faytex-orange">Legacy Reimagined</p>
            <h3 className="font-heading text-3xl md:text-4xl mt-2 max-w-md">If she can wear it during a game, you can wear it anywhere.</h3>
          </div>
        </Link>
        <Link
          to="/market-queens"
          data-testid="home-card-queens"
          className="group relative overflow-hidden rounded-3xl h-[420px]"
        >
          <img src={MARKET_IMG} alt="Makola market queen" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 text-white">
            <p className="text-xs uppercase tracking-widest text-faytex-pink">Our Market Queens</p>
            <h3 className="font-heading text-3xl md:text-4xl mt-2 max-w-md">The women of Makola, Madina & Kejetia.</h3>
          </div>
        </Link>
      </section>

      {/* CHATBOT TEASER */}
      <section className="bg-faytex-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink">Fact or Faytex?</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-2 tracking-tight">Clinically-trusted answers, with the warmth of a big sister.</h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              Ask anything about menstrual health — cycles, cramps, myths, hygiene. Our AI assistant
              gives clear, kind, judgement-free answers.
            </p>
            <Link
              to="/fact-or-faytex"
              data-testid="home-chatbot-cta"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover px-6 py-3 text-sm font-semibold transition-colors"
            >
              Ask a question <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <div className="bg-faytex-pink p-4 font-heading text-xl">Fact or Faytex?</div>
            <div className="bg-white text-faytex-ink p-6 space-y-4 text-sm">
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm p-3 max-w-[80%]">Is it normal for cramps to feel worse some months?</div>
              <div className="bg-faytex-pinkLight text-faytex-pinkHover rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto">
                Yes — it is common for cramp intensity to vary cycle to cycle due to hormones, stress, sleep and even diet. If pain consistently disrupts daily life, please see a healthcare provider.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
